import React, { useEffect, useState } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import OrangeBySiteStandaloneHeader from "@/components/OrangeBySiteStandaloneHeader";
import {
  OrangeScoreReveal, CategoriesGrid,
} from "@/optimise/AnimatedScanReveal";

const API = process.env.REACT_APP_BACKEND_URL;
const CAT_LABEL_UNUSED = null;
void CAT_LABEL_UNUSED;

function IssueList({ items, empty }) {
  if (!items || items.length === 0) return <div className="text-[13px] text-black/50">{empty}</div>;
  return (
    <ul className="space-y-2">
      {items.slice(0, 8).map((i, idx) => (
        <li key={idx} className="p-3 rounded-xl bg-[#FFF7EE] border border-[#FCE1C7]">
          <div className="font-black text-[14px]">{i.title || i.name || "Issue"}</div>
          {i.message && <div className="text-[13px] text-black/70 mt-1">{i.message}</div>}
        </li>
      ))}
    </ul>
  );
}

export default function OptimiseReport() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/api/optimise/report/${encodeURIComponent(token)}`)
      .then((r) => alive && setData(r.data))
      .catch((e) => alive && setError(e?.response?.status === 404 ? "not_found" : "error"));
    return () => { alive = false; };
  }, [token]);

  return (
    <div className="min-h-screen text-black" style={{ fontFamily: "'Manrope',ui-sans-serif,system-ui,-apple-system,sans-serif" }}>
      <div aria-hidden className="fixed inset-0 -z-10" style={{ background: "radial-gradient(ellipse 90% 80% at 58% 45%, #FFB347 0%, #FF8A2E 30%, #FF7A00 55%, #FF6A00 80%, #D94C00 100%)" }} />
      <OrangeBySiteStandaloneHeader subtitle="Orange Optimise · Report" />
      <div className="max-w-4xl mx-auto px-5 sm:px-8 pt-6 pb-24">
        {error === "not_found" && (
          <div className="bg-white border-2 border-black rounded-3xl p-8 mt-6" data-testid="optimise-report-not-found">
            <div className="text-[11px] tracking-[0.32em] uppercase font-bold text-[#FF6A00]">Report not found</div>
            <h1 className="mt-3 font-black text-4xl tracking-[-0.02em]">This report link is no longer valid.</h1>
            <p className="mt-3 text-[15px] text-black/70 max-w-xl">The report may have been revoked or expired. If you need a fresh copy, just reply to your last Orange Optimise email and we'll sort it.</p>
          </div>
        )}
        {error === "error" && <div className="mt-6 text-white">Something went wrong. Please try again shortly.</div>}
        {!data && !error && <div className="mt-6 text-white/80" data-testid="optimise-report-loading">Loading your report…</div>}

        {data && (
          <div className="space-y-6 mt-6" data-testid="optimise-report">
            <div className="bg-white border-2 border-black rounded-3xl p-6 md:p-10">
              <div className="text-[11px] tracking-[0.32em] uppercase font-bold">Orange Optimise · Your website health report</div>
              <h1 className="mt-3 font-black text-4xl md:text-5xl tracking-[-0.02em]" data-testid="optimise-report-domain">{data.domain || "your website"}</h1>
              <div className="mt-2 text-[13px] text-black/70">
                {data.plan_name} · Monitoring <strong>{data.monitoring_status}</strong> · Generated {data.created_at ? new Date(data.created_at).toLocaleString() : "—"}
              </div>

              <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="rounded-2xl bg-[#FFF7EE] border border-[#FCE1C7] p-5 md:col-span-2">
                  <OrangeScoreReveal
                    score={data.score}
                    band={data.health_band || (data.score >= 80 ? "good" : data.score >= 50 ? "needs_attention" : "poor")}
                    statusLabel={data.health_band ? data.health_band.replace("_", " ").toUpperCase() : null}
                    revealKey={data.token || data.created_at || data.score}
                    size="large"
                    testId="optimise-report-score-reveal"
                  />
                  {data.score_delta != null && data.score_delta !== 0 && (
                    <div className="mt-2 font-black text-lg" style={{ color: data.score_delta > 0 ? "#0FA958" : "#C1350B" }}>
                      {data.score_delta > 0 ? "▲" : "▼"} {Math.abs(data.score_delta)}
                    </div>
                  )}
                </div>
                <div className="rounded-2xl bg-white border border-[#EAEAEA] p-5">
                  <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#FF6A00]">Cadence</div>
                  <div className="mt-2 text-[13px] text-black/70">
                    <div><strong className="text-black">Last scan:</strong> {data.last_scan_at ? new Date(data.last_scan_at).toLocaleString() : "—"}</div>
                    <div className="mt-1"><strong className="text-black">Next scan:</strong> {data.next_scan_at ? new Date(data.next_scan_at).toLocaleString() : "—"}</div>
                  </div>
                </div>
              </div>
            </div>

            {data.categories && Object.keys(data.categories).length > 0 && (
              <div className="bg-white border border-[#EAEAEA] rounded-3xl p-6 md:p-8" data-testid="optimise-report-categories">
                <div className="text-[11px] tracking-[0.32em] uppercase font-bold text-[#FF6A00]">Category breakdown</div>
                <div className="mt-4">
                  <CategoriesGrid
                    categories={data.categories}
                    revealKey={data.token || data.created_at || data.score}
                    columns="grid-cols-2 md:grid-cols-3"
                    testIdPrefix="optimise-report-cat"
                  />
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white border border-[#EAEAEA] rounded-3xl p-6" data-testid="optimise-report-open">
                <div className="text-[11px] tracking-[0.32em] uppercase font-bold text-[#C1350B]">Current issues</div>
                <div className="mt-1 text-[11px] text-black/50 tracking-[0.08em] uppercase font-bold">
                  {(data.lifecycle_summary?.open_count ?? 0)} open
                </div>
                <div className="mt-4"><IssueList items={data?.lifecycle_open || data?.issue_diff?.new} empty="Nothing new — nice work." /></div>
              </div>
              <div className="bg-white border border-[#EAEAEA] rounded-3xl p-6" data-testid="optimise-report-resolved">
                <div className="text-[11px] tracking-[0.32em] uppercase font-bold text-[#0FA958]">Resolved</div>
                <div className="mt-1 text-[11px] text-black/50 tracking-[0.08em] uppercase font-bold">
                  {(data.lifecycle_summary?.resolved_count ?? 0)} resolved
                </div>
                <div className="mt-4"><IssueList items={data?.lifecycle_resolved || data?.issue_diff?.resolved} empty="Nothing resolved yet." /></div>
              </div>
              <div className="bg-white border border-[#EAEAEA] rounded-3xl p-6" data-testid="optimise-report-regressed">
                <div className="text-[11px] tracking-[0.32em] uppercase font-bold text-[#8A2E00]">Regressed</div>
                <div className="mt-1 text-[11px] text-black/50 tracking-[0.08em] uppercase font-bold">
                  {(data.lifecycle_summary?.regressed_count ?? 0)} regressed
                </div>
                <div className="mt-4"><IssueList items={data?.lifecycle_regressed} empty="No regressions detected." /></div>
              </div>
            </div>

            {Array.isArray(data.history) && data.history.length > 0 && (
              <div className="bg-white border border-[#EAEAEA] rounded-3xl p-6 md:p-8" data-testid="optimise-report-history">
                <div className="flex items-baseline justify-between gap-3 flex-wrap">
                  <div>
                    <div className="text-[11px] tracking-[0.32em] uppercase font-bold text-[#FF6A00]">Score history</div>
                    <h3 className="mt-1 font-black text-xl tracking-[-0.01em]">How your Orange Score has moved</h3>
                  </div>
                  {data.history.some((h) => (h.score_version || "v1") !== (data.history[data.history.length - 1].score_version || "v1")) && (
                    <span className="text-[11px] text-black/60" data-testid="optimise-report-history-v2-note">
                      Older scans used a previous scoring method — comparisons across the dashed line are not like-for-like.
                    </span>
                  )}
                </div>
                <ol className="mt-4 space-y-1 text-[13px] text-black/80" data-testid="optimise-report-history-list">
                  {data.history.map((h, i) => (
                    <li key={h.scan_id || i} className="flex items-center gap-3">
                      <span className="tabular-nums w-24 text-black/60">{h.completed_at ? new Date(h.completed_at).toLocaleDateString() : "—"}</span>
                      <span className="tabular-nums font-black text-base w-10 text-right">{h.orange_score ?? "—"}</span>
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-black tracking-[0.08em] uppercase"
                            style={{ background: (h.score_version === "v2") ? "#FFF7EE" : "#F6F5F2",
                                     color: (h.score_version === "v2") ? "#8A2E00" : "#8A8A8A",
                                     border: `1px solid ${(h.score_version === "v2") ? "#FCE1C7" : "#EAEAEA"}` }}>
                        {h.score_version === "v2" ? "v2" : "v1"}
                      </span>
                    </li>
                  ))}
                </ol>
              </div>
            )}

            <div className="bg-white border border-[#EAEAEA] rounded-3xl p-6" data-testid="optimise-report-priorities">
              <div className="flex items-baseline justify-between gap-3 flex-wrap">
                <div className="text-[11px] tracking-[0.32em] uppercase font-bold text-[#FF6A00]">What needs your attention</div>
                <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-[10px] font-black tracking-[0.16em] uppercase text-[#8A2E00] bg-[#FFF7EE] border border-[#FCE1C7]" data-testid="optimise-report-fix-note">
                  Fix available · Orange Fix quoted separately
                </span>
              </div>
              <div className="mt-4"><IssueList items={data?.top_priorities} empty="Nothing critical right now." /></div>
            </div>

            <div className="bg-white border border-[#EAEAEA] rounded-3xl p-6" data-testid="optimise-report-positives">
              <div className="text-[11px] tracking-[0.32em] uppercase font-bold text-[#0FA958]">The good news</div>
              <div className="mt-4"><IssueList items={data?.positives} empty="We'll flag genuine wins here as they appear." /></div>
            </div>

            <div className="bg-white border border-[#EAEAEA] rounded-3xl p-6 text-[13px] text-black/70">
              <strong className="text-black">What happens next?</strong> Orange Optimise continues to monitor your website. Your next scan is scheduled for {data.next_scan_at ? new Date(data.next_scan_at).toLocaleString() : "shortly"}.
              <div className="mt-2">Orange Optimise monitors and diagnoses your website. Changes requiring access to your website, hosting, CMS or code are not included and can be completed by your existing developer or quoted separately through Orange Fix.</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
